import React, { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Input } from './ui/input';
import { Button } from './ui/button';
import { Select } from './ui/select';
import { getQueryFn } from '../lib/queryClient';
import type { Country } from '../types';

interface Scope2FormProps {
  onSubmit: (data: any) => void;
  isSubmitting?: boolean;
  selectedCompanyId?: number;
}

// Note: Regions array removed - no longer needed

export function Scope2Form({ onSubmit, isSubmitting = false, selectedCompanyId }: Scope2FormProps) {
  // Fetch company countries
  const { data: companyCountries } = useQuery<Country[]>({
    queryKey: selectedCompanyId ? [`/api/countries/company/${selectedCompanyId}`] : [null],
    queryFn: getQueryFn(),
    enabled: !!selectedCompanyId,
  });

  // Form state
  const [country, setCountry] = useState('');
  const [unit, setUnit] = useState('kWh');
  const [quantity, setQuantity] = useState('');
  const [reportingYear, setReportingYear] = useState(new Date().getFullYear());
  
  // Fixed energy source for Scope 2 (electricity only)
  const energySource = 'electricity';

  // Meta
  const [conversionRate, setConversionRate] = useState<number>(1);
  const [emissionFactor, setEmissionFactor] = useState<string>('');
  const [emissionFactorSource, setEmissionFactorSource] = useState<string>('');
  const [standardQuantity, setStandardQuantity] = useState<number | null>(null);
  const [co2ePreview, setCo2ePreview] = useState<number | null>(null);

  // Note: Region and country fetching has been removed
  // Note: Energy sources fetching removed - only electricity is supported

  // Fetch meta when energy source+unit set
  const { data: energyMeta } = useQuery({
    queryKey: energySource && unit ? [
      `/api/scope2/energy-meta?energy_source=${encodeURIComponent(energySource)}&unit=${encodeURIComponent(unit)}`
    ] : [null],
    queryFn: getQueryFn(),
    enabled: !!energySource && !!unit,
  });

  // Fetch electricity emission factor when country is selected and energy source is electricity
  const { data: electricityFactor } = useQuery({
    queryKey: country && energySource === 'electricity' ? [
      `/api/scope2/electricity-emission-factor?country_code=${encodeURIComponent(country)}`
    ] : [null],
    queryFn: getQueryFn(),
    enabled: !!country && energySource === 'electricity',
  });

  // Note: Unit initialization removed - electricity always uses kWh

  // Apply meta
  useEffect(() => {
    if (energyMeta) {
      setConversionRate(energyMeta.conversion_rate ?? 1);
      setEmissionFactor(String(energyMeta.emission_factor ?? ''));
      setEmissionFactorSource(energyMeta.emission_factor_source ?? '');
    }
  }, [energyMeta]);

  // Apply electricity emission factor when available
  useEffect(() => {
    if (electricityFactor && energySource === 'electricity') {
      setEmissionFactor(String(electricityFactor.emission_factor ?? ''));
      setEmissionFactorSource(electricityFactor.source ?? 'Local Grid Emission Factor');
    }
  }, [electricityFactor, energySource]);

  // Live compute
  useEffect(() => {
    const qty = parseFloat(quantity);
    const ef = parseFloat(emissionFactor);
    const convQty = isFiniteNumber(qty) ? qty * (conversionRate || 1) : NaN;
    setStandardQuantity(isFiniteNumber(convQty) ? convQty : null);
    if (isFiniteNumber(convQty) && isFiniteNumber(ef)) setCo2ePreview(convQty * ef);
    else setCo2ePreview(null);
  }, [quantity, emissionFactor, conversionRate]);

  function isFiniteNumber(n: any) {
    return typeof n === 'number' && isFinite(n);
  }

  // Note: Region change effect has been removed
  // Note: Energy and unit options removed - only electricity/kWh supported

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const qty = parseFloat(quantity);
    const ef = parseFloat(emissionFactor);
    const convQty = isFiniteNumber(qty) ? qty * (conversionRate || 1) : 0;
    const computedCO2e = isFiniteNumber(convQty) && isFiniteNumber(ef) ? convQty * ef : undefined;

    onSubmit({
      scope: 'scope2',
      activity_type: energySource,
      quantity: qty,
      unit: unit,
      emission_factor: ef,
      emission_factor_source: emissionFactorSource || undefined,
      is_estimated: false,
      // Note: region has been removed
      country,
      reporting_year: reportingYear,
      standard_value: convQty,
      co2_equivalent: computedCO2e,
      notes: undefined,
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Select
          label="Country"
          value={country}
          options={[
            { value: '', label: 'Select country…' },
            ...(companyCountries || []).map((c: Country) => ({ value: c.code, label: c.name }))
          ]}
          onChange={(e: any) => setCountry(e?.target ? e.target.value : e)}
        />
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Energy source</label>
          <div className="text-sm text-gray-600 border border-dashed rounded-md p-2 bg-gray-50">
            Electricity
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Unit</label>
          <div className="text-sm text-gray-600 border border-dashed rounded-md p-2 bg-gray-50">
            kWh
          </div>
        </div>
        <Input
          label="Quantity"
          type="number"
          step="0.0001"
          value={quantity}
          onChange={(e) => setQuantity(e.target.value)}
          required
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Input
          label="Reporting Year"
          type="number"
          min="2000"
          max="2100"
          value={reportingYear}
          onChange={(e) => setReportingYear(parseInt(e.target.value) || new Date().getFullYear())}
          required
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Input
          label="Emission factor (kg CO2e per standard unit)"
          placeholder="Auto-fetched"
          type="number"
          step="0.0001"
          value={emissionFactor}
          onChange={(e) => setEmissionFactor(e.target.value)}
          required
        />
        <div className="space-y-1">
          <label className="block text-sm font-medium text-gray-700">Standard Quantity (Quantity × conversion)</label>
          <div className="text-sm text-gray-600 border border-dashed rounded-md p-2">
            {standardQuantity !== null ? standardQuantity.toFixed(4) : '—'}
          </div>
        </div>
      </div>

      {co2ePreview !== null && (
        <div className="text-lg font-bold text-green-800 bg-green-50 px-4 py-3 rounded-lg border-2 border-green-200">
          Preview CO₂e: {co2ePreview.toFixed(4)} kg CO₂e
        </div>
      )}

      <div className="flex justify-end">
        <Button type="submit" disabled={isSubmitting}>
          {isSubmitting ? 'Submitting…' : 'Save Measured Entry'}
        </Button>
      </div>
    </form>
  );
}
